// Schedule Intro Page - Stages 02_INTEREST_CONFIRMED and 03_GENERIC_DEMO
// Handles intro meeting booking, display, reschedule/cancel
// UPDATED: Added polling for backend sync and blur/focus handling for reschedule/cancel
// UPDATED: Added demo mode support
import { useEffect, useRef, useMemo } from 'react'
import { InlineWidget } from 'react-calendly'
import { useOnboarding } from '../../context/OnboardingContext'
import { useAuth } from '../../context/AuthContext'
import { useDemoMode } from '../../hooks/useDemoMode'
import { n8nApi } from '../../services/n8nApi'
import { CALENDLY_CONFIG, getUtmCampaign } from '../../config/calendly'

const ScheduleIntroPage = () => {
  const { prospectData, currentStage, STAGES, effectiveUid } = useOnboarding()
  const { user } = useAuth()
  const { isDemoMode, demoStage, exitDemo } = useDemoMode()

  // Track whether we're awaiting return from external Calendly page
  const isAwaitingExternalAction = useRef(false)

  // In demo mode, show "booked" state for stage 03
  const hasIntroScheduled = useMemo(() => {
    if (isDemoMode && demoStage === '03_GENERIC_DEMO') {
      return true
    }
    return prospectData.introMeetingScheduled && prospectData.introMeetingScheduledAt
  }, [isDemoMode, demoStage, prospectData.introMeetingScheduled, prospectData.introMeetingScheduledAt])

  // Listen for Calendly events AND window blur/focus (for reschedule/cancel)
  useEffect(() => {
    // DEMO MODE: Skip all event listeners and polling
    if (isDemoMode) return

    // Store original values to detect changes
    const originalStatus = prospectData.introMeetingScheduled
    const originalTime = prospectData.introMeetingScheduledAt

    // Poll until data changes or max attempts reached
    const pollUntilSynced = async () => {
      const maxAttempts = 10
      const pollInterval = 1000
      
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval))
        
        try {
          const response = await n8nApi.getUserStatus(effectiveUid)
          
          if (response.found && response.prospect) {
            const newStatus = response.prospect.intro_meeting_scheduled
            const newTime = response.prospect.intro_meeting_scheduled_at
            
            // Check if anything changed
            if (newStatus !== originalStatus || newTime !== originalTime) {
              console.log(`✅ Change detected after ${attempt} attempts`)
              window.location.reload()
              return
            }
          }
          
          console.log(`⏳ Attempt ${attempt}/${maxAttempts} - no change yet`)
        } catch (err) {
          console.error('Poll error:', err)
        }
      }
      
      console.log('⚠️ Max attempts reached, no change detected')
    }

    // Handle embedded widget events (new bookings)
    const handleCalendlyEvent = async (e) => {
      if (e.origin !== 'https://calendly.com') return
      
      const data = e.data
      console.log('📨 Calendly event received:', data)
      
      if (data.event === 'calendly.event_scheduled') {
        console.log('✅ Intro meeting booked! Waiting for backend sync...')
        await pollUntilSynced()
      }
    }

    // Handle user leaving the page (clicking reschedule/cancel links)
    const handleWindowBlur = () => {
      if (hasIntroScheduled) {
        // User clicked away while meeting was scheduled (likely reschedule/cancel link)
        isAwaitingExternalAction.current = true
        console.log('👋 Window lost focus, will check for updates on return')
      }
    }

    // Handle returning from external Calendly pages (reschedule/cancel)
    const handleWindowFocus = async () => {
      if (!isAwaitingExternalAction.current) return
      
      console.log('🔄 Window refocused after external action, checking for updates...')
      isAwaitingExternalAction.current = false
      await pollUntilSynced()
    }

    window.addEventListener('message', handleCalendlyEvent)
    window.addEventListener('blur', handleWindowBlur)
    window.addEventListener('focus', handleWindowFocus)
    
    return () => {
      window.removeEventListener('message', handleCalendlyEvent)
      window.removeEventListener('blur', handleWindowBlur)
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [effectiveUid, hasIntroScheduled, prospectData.introMeetingScheduled, prospectData.introMeetingScheduledAt, isDemoMode])

  const formatMeetingTime = (isoString) => {
    if (!isoString) return null
    const date = new Date(isoString)
    return {
      date: date.toLocaleDateString(undefined, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString(undefined, { 
        hour: 'numeric', 
        minute: '2-digit',
        timeZoneName: 'short'
      })
    }
  }

  // In demo mode, generate mock meeting time (tomorrow at 2pm)
  const meetingTime = useMemo(() => {
    if (!hasIntroScheduled) return null

    if (isDemoMode) {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(14, 0, 0, 0)
      return formatMeetingTime(tomorrow.toISOString())
    }

    return formatMeetingTime(prospectData.introMeetingScheduledAt)
  }, [hasIntroScheduled, isDemoMode, prospectData.introMeetingScheduledAt])

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-strong">
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-950">
          {hasIntroScheduled 
            ? `Your Intro Call is Booked, ${prospectData.fullName?.split(' ')[0] || 'there'}!`
            : `Let's Get Started, ${prospectData.fullName?.split(' ')[0] || 'there'}!`
          }
        </h1>
        <p className="mt-3 text-slate-600">
          {hasIntroScheduled 
            ? 'We look forward to discussing how FleetVision can help reduce your fleet risk exposure.'
            : 'Book a 30-minute intro call with Robert Putt to discuss your fleet\'s risk profile and see how FleetVision prevents nuclear claims.'
          }
        </p>
      </div>

      {/* What to expect */}
      {!hasIntroScheduled && (
        <div className="space-y-4 mb-8">
          <div className="card">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                <span className="text-brand-700 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-brand-950">See FleetVision in action</h3>
                <p className="text-slate-600 mt-1">
                  Robert will walk you through how our AI identifies nuclear claim risks 
                  using real dashcam footage.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                <span className="text-brand-700 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-brand-950">Discuss your fleet profile</h3>
                <p className="text-slate-600 mt-1">
                  We'll learn about your fleet size, operations, and current insurance challenges.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                <span className="text-brand-700 font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-brand-950">Explore next steps</h3>
                <p className="text-slate-600 mt-1">
                  If FleetVision looks like a fit, we can discuss connecting with your dashcam video library
                  and API for a personalised risk analysis.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meeting scheduled confirmation */}
      {hasIntroScheduled && meetingTime ? (
        <div className="card mb-8 bg-accent-50 border-accent-200">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-accent-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-accent-900 mb-1">
                Intro Call Confirmed
              </h2>
              <p className="text-sm text-accent-700">
                You'll receive calendar invitations and reminders via email.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 border border-accent-200">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="font-semibold text-brand-950 text-lg">{meetingTime.date}</p>
                <p className="text-slate-600">{meetingTime.time}</p>
              </div>
            </div>

            {/* Hide reschedule/cancel in demo mode */}
            {!isDemoMode && (
              <div className="flex flex-col sm:flex-row gap-3">
                {prospectData.introCalendlyRescheduleUrl && (
                  <a
                    href={prospectData.introCalendlyRescheduleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary flex-1 justify-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Reschedule
                  </a>
                )}
                {prospectData.introCalendlyCancelUrl && (
                  <a
                    href={prospectData.introCalendlyCancelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex-1 justify-center text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel
                  </a>
                )}
              </div>
            )}
          </div>

          {/* What happens after the call */}
          <div className="mt-6 p-4 bg-white rounded-lg border border-accent-200">
            <h3 className="font-semibold text-brand-950 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              What happens after the call?
            </h3>
            <p className="text-sm text-slate-600">
              If FleetVision looks like a good fit for your fleet, Robert will guide you through 
              connecting your dashcam account. We'll then analyse your footage and 
              schedule a personalised walkthrough of your risk analysis.
            </p>
          </div>
        </div>
      ) : (
        /* Calendly Widget for booking */
        <div className="card mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-brand-950">
                Book Your Intro Call
              </h2>
              <p className="text-sm text-slate-600">
                30 minutes with Robert
              </p>
            </div>
          </div>
          
          <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
            <InlineWidget
              url={CALENDLY_CONFIG.intro.eventUrl}
              prefill={{
                name: prospectData.fullName || '',
                email: prospectData.email || user?.email || '',
                customAnswers: {
                  a1: prospectData.companyName || ''
                }
              }}
              utm={{
                utmSource: CALENDLY_CONFIG.intro.utmSource,
                utmMedium: CALENDLY_CONFIG.intro.utmMedium,
                utmCampaign: getUtmCampaign(effectiveUid)
              }}
              styles={CALENDLY_CONFIG.styles}
            />
          </div>
        </div>
      )}

      {/* Quick contact options */}
      <div className="bg-slate-50 rounded-xl p-6 mb-8 border border-slate-200">
        <h2 className="font-semibold text-brand-950 mb-4">Can't find a suitable time?</h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="mailto:sales@insurevision.ai"
            className="btn-secondary flex-1 justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email Us
          </a>
          <a
            href="tel:+447976454426"
            className="btn-primary flex-1 justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Call Us
          </a>
        </div>
      </div>

      {/* Confirmation footer */}
      <div className="text-center text-slate-500 text-sm">
        <p>
          Confirmation emails will be sent to{' '}
          <span className="font-medium text-slate-700">{prospectData.email || user?.email || 'your email'}</span>
        </p>
      </div>
    </div>
  )
}

export default ScheduleIntroPage
